import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { FileText, Download, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import * as XLSX from "xlsx";
import { downloadBlob } from "@/utils/download";

export function RelatorioTab() {
  const { authAxios } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get(`/timeclock/report?month=${month}`);
      setReport(res.data);
    } catch {}
    finally { setLoading(false); }
  }, [authAxios, month]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const exportPDF = () => {
    if (!report) return;
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.setTextColor(5, 150, 105);
    doc.text("KAISO SYSTEM", 14, 20);
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(`Relatório de Ponto - ${month}`, 14, 30);
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Gerado em: ${new Date().toLocaleDateString("pt-BR")}`, 14, 37);

    if (report.members.length > 0) {
      autoTable(doc, {
        startY: 45,
        head: [["Funcionário", "Dias", "Horas Totais", "Horas Extra", "Horas/Dia (média)"]],
        body: report.members.map(m => [
          m.member_name, m.days_worked,
          `${m.total_hours.toFixed(1)}h`, `${m.total_overtime.toFixed(1)}h`,
          m.days_worked > 0 ? `${(m.total_hours / m.days_worked).toFixed(1)}h` : "-",
        ]),
        theme: "striped",
        headStyles: { fillColor: [5, 150, 105] },
        margin: { left: 14 },
      });
      let y = doc.lastAutoTable.finalY + 15;
      for (const m of report.members) {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.text(`${m.member_name} - Detalhe`, 14, y);
        autoTable(doc, {
          startY: y + 3,
          head: [["Data", "Entrada", "Saída", "Horas", "Extra"]],
          body: m.records.map(r => [
            r.date,
            r.clock_in ? new Date(r.clock_in).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "-",
            r.clock_out ? new Date(r.clock_out).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "-",
            `${r.hours_worked.toFixed(1)}h`,
            r.overtime_hours > 0 ? `+${r.overtime_hours.toFixed(1)}h` : "-",
          ]),
          theme: "striped",
          headStyles: { fillColor: [100, 100, 100] },
          styles: { fontSize: 9 },
          margin: { left: 14 },
        });
        y = doc.lastAutoTable.finalY + 10;
      }
    }
    downloadBlob(doc.output("blob"), `Ponto_${month}.pdf`);
    toast.success("PDF exportado!");
  };

  const exportExcelReport = () => {
    if (!report) return;
    const rows = [];
    for (const m of report.members) {
      for (const r of m.records) {
        rows.push({
          Funcionário: m.member_name, Data: r.date,
          Entrada: r.clock_in ? new Date(r.clock_in).toLocaleTimeString("pt-BR") : "",
          Saída: r.clock_out ? new Date(r.clock_out).toLocaleTimeString("pt-BR") : "",
          "Horas Trabalhadas": r.hours_worked, "Horas Extra": r.overtime_hours,
        });
      }
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ponto");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    downloadBlob(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `Ponto_${month}.xlsx`);
    toast.success("Excel exportado!");
  };

  if (loading) return <LoadingScreen />;
  if (!report) return <p className="text-center py-8 text-muted-foreground">Erro ao carregar</p>;

  const totalHours = report.members.reduce((s, m) => s + m.total_hours, 0);
  const totalOvertime = report.members.reduce((s, m) => s + m.total_overtime, 0);

  return (
    <div className="space-y-4" data-testid="relatorio-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">Relatório mensal para contabilidade</p>
        <div className="flex gap-2 items-center">
          <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={exportPDF} data-testid="export-ponto-pdf"><FileText className="w-3 h-3" />PDF</Button>
          <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={exportExcelReport} data-testid="export-ponto-excel"><Download className="w-3 h-3" />Excel</Button>
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-44" data-testid="report-month" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-card border-border/50"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium mb-1">Funcionários</p>
          <span className="text-2xl font-black">{report.members.length}</span>
        </CardContent></Card>
        <Card className="bg-card border-border/50"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium mb-1">Total Registros</p>
          <span className="text-2xl font-black">{report.total_records}</span>
        </CardContent></Card>
        <Card className="bg-card border-border/50"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium mb-1">Total Horas</p>
          <span className="text-2xl font-black text-emerald-600">{totalHours.toFixed(1)}h</span>
        </CardContent></Card>
        <Card className="bg-card border-border/50"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground font-medium mb-1">Horas Extra</p>
          <span className="text-2xl font-black text-amber-600">{totalOvertime.toFixed(1)}h</span>
        </CardContent></Card>
      </div>

      <div className="space-y-3">
        {report.members.map(m => (
          <Card key={m.member_id} className="border-border/50" data-testid={`member-report-${m.member_id?.slice(-6)}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                    <span className="text-sm font-bold text-emerald-700">{m.member_name?.charAt(0)?.toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="font-bold text-sm">{m.member_name}</p>
                    <p className="text-xs text-muted-foreground">{m.days_worked} dias trabalhados</p>
                  </div>
                </div>
                <div className="flex gap-4 text-right">
                  <div><p className="text-xs text-muted-foreground">Total</p><p className="font-bold text-emerald-700">{m.total_hours.toFixed(1)}h</p></div>
                  <div><p className="text-xs text-muted-foreground">Extra</p><p className={`font-bold ${m.total_overtime > 0 ? "text-amber-600" : "text-muted-foreground"}`}>{m.total_overtime.toFixed(1)}h</p></div>
                  <div><p className="text-xs text-muted-foreground">Média/dia</p><p className="font-bold">{m.days_worked > 0 ? (m.total_hours / m.days_worked).toFixed(1) : 0}h</p></div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {report.members.length === 0 && (
          <Card><CardContent className="py-12 text-center text-muted-foreground">
            <CalendarDays className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
            <p>Sem registros neste mês</p>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
